import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { request, useOperations } from './OperationsProvider';
import { exportCsv, parseCsv } from './csv';
import { IMPORTS, readRows } from './importSpecs';

// Loading a register from a spreadsheet.
//
// One dialog for every register that takes a file, because the thing being done is the same each
// time and a plant should not have to learn it twice. What differs - the columns, the template, the
// sentence explaining them - comes from the description of the register, not from a copy of this.
//
// The file is all or nothing at the server, and this says so before anybody presses the button: a
// register half loaded is the outcome nobody can unpick.
export default function ImportRecords({ kind, onClose }) {
  const spec = IMPORTS[kind];
  const { refresh } = useOperations();
  const [rows, setRows] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(false);

  const read = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setRows(null); setResult(null); setFileError(null);
    file.text().then((text) => {
      const { rows: read_, missing } = readRows(kind, parseCsv(text));
      if (missing) {
        setFileError(`The first row must name the columns, including ${missing.join(', ')}. Download the template to start from it.`);
        return;
      }
      if (!read_.length) {
        setFileError('The file has a header row and nothing under it.');
        return;
      }
      setRows(read_);
    }).catch(() => setFileError('The file could not be read.'));
  };

  const submit = async () => {
    setPending(true); setResult(null);
    try {
      const response = await request(`/api/operations/${kind}/import`, 'POST', { rows });
      setResult(response);
      if (response.created) await refresh();
    } catch (e) {
      setResult({ created: 0, errors: [{ row: '—', message: e.message }] });
    } finally { setPending(false); }
  };

  const done = result?.created > 0;
  const errors = result?.errors || [];
  return (
    <Dialog open onClose={() => !pending && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{spec.title}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{spec.note}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`Columns: ${spec.headers.join(', ')}. Required: ${spec.required.join(', ')}. Nothing is imported if any row has a problem.`}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button onClick={() => exportCsv(`${kind}-import-template`, spec.headers, spec.sample)}>Download template</Button>
            <Button component="label" variant="outlined" disabled={pending}>
              Choose CSV file
              <input type="file" hidden accept=".csv,text/csv" onChange={read} />
            </Button>
          </Stack>
          {fileError && <Alert severity="error">{fileError}</Alert>}
          {rows && !done && <Typography>{`${rows.length} ${rows.length === 1 ? 'row' : 'rows'} ready to import.`}</Typography>}
          {done && <Alert severity="success">{`${result.created} ${result.created === 1 ? 'entry' : 'entries'} added.`}</Alert>}
          {errors.length > 0 && (
            <Alert severity="error">
              {`Nothing was imported. Fix ${errors.length === 1 ? 'this row' : `these ${errors.length} rows`} and import the file again:`}
              <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 200, overflow: 'auto' }}>
                {errors.map((e) => <li key={`${e.row}-${e.message}`}>{`Row ${e.row}: ${e.message}`}</li>)}
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={onClose}>{done ? 'Done' : 'Cancel'}</Button>
        {!done && <Button variant="contained" disabled={pending || !rows?.length} onClick={submit}>{pending ? 'Importing…' : 'Import'}</Button>}
      </DialogActions>
    </Dialog>
  );
}
